import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import { Box, Tooltip, Typography } from '@mui/material'
import React from 'react'

import type { ImageLabel } from '@/utils/constants'

interface ImageLabelChipsProps {
  labels: ImageLabel[]
  onAddLabel: () => void
  onEditLabel: () => void
}

const MAX_VISIBLE = 3

export default function ImageLabelChips({
  labels,
  onAddLabel,
  onEditLabel,
}: ImageLabelChipsProps) {
  const visibleLabels = labels.slice(0, MAX_VISIBLE)
  const overflowLabels = labels.slice(MAX_VISIBLE)
  const overflowCount = overflowLabels.length

  if (labels.length === 0) {
    return (
      <Box sx={{ mt: 1 }}>
        <Box
          id="add-label-btn"
          onClick={onAddLabel}
          display="inline-flex"
          alignItems="center"
          gap={0.5}
          sx={{
            border: '1px solid #68cea1',
            borderRadius: '4px',
            px: 1,
            py: 0.25,
            cursor: 'pointer',
            color: '#68cea1',
            fontSize: '11px',
            fontWeight: 500,
            letterSpacing: '0.5px',
            userSelect: 'none',
            transition: 'all 0.15s',
            '&:hover': {
              bgcolor: 'rgba(104, 206, 161, 0.08)',
            },
          }}
        >
          <AddIcon sx={{ fontSize: 13 }} />
          ADD LABEL
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ mt: 1 }}>
      {/* Label chips row */}
      <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
        {visibleLabels.map((label) => (
          <Tooltip key={label.id} title={label.name} placement="top" arrow>
            <Box
              sx={{
                bgcolor: '#68CEA1',
                border: 'none',
                borderRadius: '8px',
                px: 0.75,
                py: 0.25,
                fontSize: '11px',
                color: '#000000',
                maxWidth: '90px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                cursor: 'default',
                lineHeight: 1.4,
              }}
            >
              {label.name}
            </Box>
          </Tooltip>
        ))}

        {overflowCount > 0 && (
          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                {overflowLabels.map((l) => (
                  <Typography
                    key={l.id}
                    sx={{ fontSize: '12px', lineHeight: 1.6 }}
                  >
                    {l.name}
                  </Typography>
                ))}
              </Box>
            }
            placement="top"
            arrow
          >
            <Box
              sx={{
                bgcolor: '#68CEA1',
                border: 'none',
                borderRadius: '8px',
                px: 0.75,
                py: 0.25,
                fontSize: '11px',
                color: '#000000',
                cursor: 'pointer',
                lineHeight: 1.4,
                fontWeight: 600,
              }}
            >
              +{overflowCount}
            </Box>
          </Tooltip>
        )}
      </Box>

      {/* Edit Label button */}
      <Box
        id="edit-label-btn"
        onClick={onEditLabel}
        display="inline-flex"
        alignItems="center"
        gap={0.5}
        sx={{
          mt: 0.75,
          cursor: 'pointer',
          color: '#68cea1',
          fontSize: '11px',
          fontWeight: 500,
          letterSpacing: '0.4px',
          border: '1px solid #68cea1',
          borderRadius: '4px',
          px: 0.75,
          py: 0.25,
          userSelect: 'none',
          transition: 'all 0.15s',
          '&:hover': {
            bgcolor: 'rgba(104, 206, 161, 0.08)',
          },
        }}
      >
        <EditIcon sx={{ fontSize: 11 }} />
        EDIT LABEL
      </Box>
    </Box>
  )
}
